import React from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';
import { bindActionCreators } from 'redux';

import ExternalLink from 'components/generic/ExternalLink.component';
import LINK from 'constants/link.const';
import { useCheckoutPermissions } from 'hooks/useCheckoutPermissions.hook';
import { useHelpLinks } from 'hooks/useHelpLinks.hook';
import { useTranslation, withTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { withTheme } from 'providers/Theme.provider';
import { selectCurrentSalonHasSelfEmployedStripeAccount } from 'selectors/currentSalon.selectors';
import getArray from 'utils/accessors/getArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import isEmptyString from 'utils/predicates/string/isEmptyString.util';

import { addProduct, addService, removeProduct, removeService, updateProductPrice, updateProductTitle } from 'checkout/actions';
import CheckoutProduct from 'checkout/components/CheckoutProduct';
import CheckoutService from 'checkout/components/CheckoutService';

import { AddClientButton } from './AddClientButton.component';

import CN from './CheckoutClient.module.scss';

const L = logger('CheckoutClient'); // eslint-disable-line no-unused-vars

const USE_TIP_BUTTON_WITH_CONDITIONS = false;
const USE_TIP_BUTTON_FOR_TEMPORALY_TEST_BE = true;

const ProductsMessage = () => {
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();
  const { bulkEditingProductsHelpLink } = useHelpLinks();

  return (
    <div className={CN.dropdownMessage}>
      <div className={CN.dropdownTitle}>{t('checkout.productsMessage.title')}</div>
      <div className={CN.highlighted}>
        {t('checkout.productsMessage.descriptionPart1')} <div className={CN.textBold}>{t('checkout.productsMessage.descriptionPart2')}</div>
        {t('checkout.productsMessage.descriptionPart3')}
        <NavLink to={linkTo({ pattern: LINK.stocksManager, params: { salonId }, fault: '#' })} activeClassName={CN.selected}>
          <div className={CN.textUnderlined}>{t('checkout.productsMessage.linkLabel1')}</div>
        </NavLink>
        {t('checkout.productsMessage.descriptionPart4')}
      </div>
      <div className={CN.linkContainer}>
        <ExternalLink link={bulkEditingProductsHelpLink} variant="inline">
          <div className={CN.textUnderlined}>{t('checkout.productsMessage.linkLabel2')}</div>
        </ExternalLink>
      </div>
    </div>
  );
};

class CheckoutClient extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      serviceSearchTerm: '',
      productSearchTerm: '',
      filteredServices: [],
      filteredProducts: [],
      selectedService: null,
      selectedProduct: null,
      productRow: false,
      clearBtn: false,
      clearServiceBtn: false,
      showMoreClientNotes: false,
      showMoreBookingNotes: false,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.character);

    const { fetchNextAppointmentDetails, client, bookingData, salonId } = this.props;

    if (client?.id && bookingData?.appointment?.id) {
      const clientId = typeof client.id === 'number' ? client.id : Number(client.id);
      const promise = fetchNextAppointmentDetails(salonId, clientId, bookingData.appointment.id);
      L.unignore('componentDidMount()', 'fetchNextAppointmentDetails:', promise);
    } else if ('Walk-In' === client?.user_name) {
      this.showProductRow();
    }

    setTimeout(() => {
      const div = document.querySelector('.checkout-add-booking');
      if (div) {
        div.scrollIntoView();
        window.scrollBy(0, -100);
      }
    }, 100);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.character);
  }

  createProduct = () => {
    this.addProduct({
      title: this.state.productSearchTerm,
      price: 0,
      discount: 0,
      discountType: 'absolute',
      finalPrice: 0,
      quantity: 0,
      stylistId: (this.props.bookingData.stylist.id && this.props.bookingData.stylist.id) || -1,
    });
    this.setState({ productSearchTerm: '', selectedProduct: null });
  };

  addService = (service) => void this.props.addService(this.props.index, service);
  addProduct = (product) => void this.props.addProduct(this.props.index, product);

  searchServices = (event) => {
    const { services } = this.props;
    const filterTerm = event.target.value.toLowerCase();
    const filteredServices = [];

    services.map((service) => {
      if (service.name.toLowerCase().includes(filterTerm)) {
        filteredServices.push(service);
      }
      return false;
    });

    this.setState({
      serviceSearchTerm: event.target.value,
      filteredServices,
      selectedService: null,
      clearServiceBtn: true,
    });
  };

  searchProducts = (event) => {
    const { products } = this.props;
    const filterTerm = event.target.value.toLowerCase();
    const filteredProducts = [];
    filterTerm.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    if (filterTerm.length > 1) {
      products.map((product) => {
        const productTitle = product.title
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        if (productTitle.toLowerCase().includes(filterTerm)) {
          filteredProducts.push(product);
        }
        return false;
      });
    }
    this.setState({
      productSearchTerm: event.target.value,
      filteredProducts,
      selectedProduct: null,
      clearBtn: true,
    });
  };

  clearProduct = () => this.setState({ productSearchTerm: '', filteredProducts: [] });

  selectService = (service) => {
    this.addService({
      id: service.id || 0,
      name: service.name,
      discount: 0,
      discountType: 'absolute',
      stylistId: this.findDefaultStylistId(),
      price: parseFloat(service.price),
      finalPrice: parseFloat(service.price),
      start_time: '',
      appointmentId: service.appointment ? service.appointment.id : 0,
      tax_ids: service.tax_ids,
      fee_ids: service.fee_ids,
      is_tax_exempt: service.is_tax_exempt ?? false,
      tax_exempt_reason: service.tax_exempt_reason ?? '',
    });
    this.setState({ serviceSearchTerm: '', filteredServices: [] });
  };

  clearService = () => void this.setState({ serviceSearchTerm: '', filteredServices: [] });

  selectProduct = (product) => {
    const productToAdd = {
      id: product.id,
      title: product.title,
      discount: 0,
      discountType: 'absolute',
      stylistId: this.props.addedServices[0] ? this.props.addedServices[0].stylistId : this.findDefaultStylistId(),
      price: product.price,
      finalPrice: product.price ? parseFloat(product.price.toFixed(2)) : 0,
      quantity: 1,
      tax_ids: product.tax_ids,
      fee_ids: product.fee_ids,
      is_tax_exempt: product.is_tax_exempt ?? false,
      tax_exempt_reason: product.tax_exempt_reason ?? '',
    };
    this.addProduct(productToAdd);
    this.setState({
      productSearchTerm: '',
      filteredProducts: [],
    });
  };

  sortFunc = (a, b) => moment(a.start_time) - moment(b.start_time);
  removeService = (serviceIndex, appointmentId) => void this.props.removeService(this.props.index, serviceIndex, appointmentId);

  removeProduct = (productIndex) => {
    if (this.props.addedProducts.length === 1) this.setState({ productRow: false });
    this.props.removeProduct(this.props.index, productIndex);
  };

  updateProductTitle = (productIndex, title) => void this.props.updateProductTitle(this.props.index, productIndex, title);
  updateProductPrice = (productIndex, price) => void this.props.updateProductPrice(this.props.index, productIndex, price);
  showProductRow = () => void this.setState({ productRow: true });

  // we are trying to find which stylist id to use by going through all the services that have already been added
  // we are searching for the most used id
  findDefaultStylistId = () => {
    const idsMap = {};
    let idMax = { id: 0, count: 0 };
    for (let i = 0; i < this.props.addedServices.length; i++) {
      if (idsMap[this.props.addedServices[i].stylistId]) {
        idsMap[this.props.addedServices[i].stylistId]++;
      } else {
        idsMap[this.props.addedServices[i].stylistId] = 1;
      }
      if (idsMap[this.props.addedServices[i].stylistId] > idMax.count) {
        idMax = { id: this.props.addedServices[i].stylistId, count: idsMap[this.props.addedServices[i].stylistId] };
      }
    }

    // if you still have not found a stylist id we are using the one in BookingData
    if (idMax.id) {
      return idMax.id;
    }

    if (this.props.bookingData && this.props.bookingData.stylist && this.props.bookingData.stylist.id) {
      return this.props.bookingData.stylist.id;
    }
    const firstActiveStylist = this.props.stylists.filter((s) => s.status === 'active' && s.id)[0];
    if (firstActiveStylist && firstActiveStylist.id) {
      return firstActiveStylist.id;
    }
    return 0;
  };

  toggleClientNotes = () => void this.setState({ showMoreClientNotes: !this.state.showMoreClientNotes });
  toggleBookingNotes = () => void this.setState({ showMoreBookingNotes: !this.state.showMoreBookingNotes });

  character = () => {
    if (window.innerWidth >= 1024) return 150;
    if (window.innerWidth >= 834) return 120;
    if (window.innerWidth >= 810) return 150;
    if (window.innerWidth >= 768) return 101;
    if (window.innerWidth >= 375) return 102;
    if (window.innerWidth <= 320) return 103;

    return 105;
  };

  render() {
    const {
      client,
      hasSelfStripeAccount,
      isDarkMode,
      nextAppointment,
      bookingData,
      addedServices,
      bookingNotes,
      removeClient,
      stylists,
      services,
      products,
      tipBtnVisible,
      addedProducts,
      showTip,
      index: indexFromProps,
      t,
      canApplyDiscount = true,
    } = this.props;

    const clientTags = getArray('bookingData.appointment.client.client_tags', this.props);

    const isDesktop = window.innerWidth >= 1024;
    const clientNotes = bookingData?.appointment?.client?.notes;

    const addedServicesSorted = addedServices.sort(this.sortFunc);
    const nextProceduresSorted = nextAppointment?.procedures ? nextAppointment.procedures.sort(this.sortFunc) : [];

    const isRemoveShown = fromAnyToBool(1 <= new Set(addedServicesSorted.map((item) => item.appointmentId)).size);

    const isTipButtonRendered = fromAnyToBool(hasSelfStripeAccount && 1 < new Set(addedServicesSorted.map((item) => item.stylistId)).size);
    const isFirstClient = indexFromProps === 0;

    return (
      <div
        data-bem="CheckoutClient"
        className={cn({
          'tbl-client-row': true,
          [CN.component]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        <div data-bem="CheckoutClient__toolbar" className="toolbar-row">
          <div className="name">{t('checkout.checkoutClient.client')}</div>
          <div className="next-appointment">{t('checkout.checkoutClient.nextAppointment')}</div>
        </div>

        <div data-bem="CheckoutClient__client" className="selected-client">
          <div className="name">
            <span>{`${client.user_name}`}</span>
            {bookingData &&
              clientTags.map(({ id, emoticon }, index) => (
                <div data-bem="CheckoutClient__tag" data-id={id} data-index={index} key={id} className="tag-icon">
                  {emoticon}
                </div>
              ))}
          </div>

          {fromAnyToBool(nextProceduresSorted[0]) && (
            <div className="next-appointment">
              {/* TODO: date localization */}
              <span>{moment(nextProceduresSorted[0]?.start_time).format('hh:mm a ddd  DD MMM YYYY')}</span>
              <small>{nextAppointment ? nextAppointment.title.split(',').join(', ') : ''}</small>
            </div>
          )}

          {isFirstClient && (
            <div className="add-client-button">
              <AddClientButton onAddClient={this.props.onAddHandler} buttonText={t('checkout.checkoutTitle.addAnotherClient')} />
            </div>
          )}
          <div className="btn-remove-client" role="button" tabIndex={0} onClick={() => removeClient(indexFromProps)}>
            {t('common.buttons.removeUpp')}
          </div>
        </div>

        {bookingNotes && bookingNotes.length > 0 && (
          <section data-bem="CheckoutClient__notes" className="view-notes" tabIndex={0} role="button" onClick={this.toggleBookingNotes}>
            <strong>{t('checkout.checkoutClient.bookingNotes')}&nbsp; </strong>
            {bookingNotes && bookingNotes.length >= this.character() ? (
              <>
                {this.state.showMoreBookingNotes ? bookingNotes : bookingNotes.substring(0, this.character())}
                <div className="btn-view-notes">{this.state.showMoreBookingNotes ? t('checkout.checkoutClient.viewLess') : t('checkout.checkoutClient.viewMore')}</div>
              </>
            ) : (
              <>{bookingNotes}</>
            )}
          </section>
        )}

        {clientNotes && clientNotes.length > 0 && (
          <section className="view-notes" tabIndex={0} role="button" onClick={this.toggleClientNotes}>
            <strong>{t('checkout.checkoutClient.clientNotes')}&nbsp;</strong>
            {clientNotes && clientNotes.length >= this.character() ? (
              <>
                {this.state.showMoreClientNotes ? clientNotes : clientNotes.substring(0, this.character())}
                <div className="btn-view-notes">{this.state.showMoreClientNotes ? t('checkout.checkoutClient.viewLess') : t('checkout.checkoutClient.viewMore')}</div>
              </>
            ) : (
              <>{clientNotes}</>
            )}
          </section>
        )}

        <section data-bem="CheckoutClient__price-lines-container" className="scrollable-in-tablet 1">
          <div data-bem="CheckoutClient__price-lines-head" className="table-checkout service">
            <div className="thead gray">
              <div className="cell-product">{t('checkout.checkoutClient.services')}</div>
              <div className="cell-staff">{t('checkout.checkoutClient.staffMember')}</div>
              <div className="cell-price">
                <span>{t('checkout.checkoutClient.price')}</span>
              </div>
              {canApplyDiscount && <div className="cell-discount">{t('checkout.checkoutClient.discount')}</div>}
              <div className="cell-total in-header">{t('checkout.checkoutClient.total')}</div>
              <div className="cell-last" />
            </div>
            <div data-bem="CheckoutClient__price-lines-body" className="tbody">
              {/* Add service row */}
              {addedServicesSorted.length > 0 &&
                addedServicesSorted.map((service, i) => (
                  <CheckoutService
                    service={service}
                    key={service.id ? `${i}${service.id}` : `${i}${service.price}`}
                    index={i}
                    clientIndex={indexFromProps}
                    onDelete={() => this.removeService(i, service.appointmentId)}
                    services={services}
                    stylists={stylists}
                    showRemove={isRemoveShown}
                  />
                ))}
              <div className="tr mobile-view-product">
                <div className="cell-product">
                  <div className="select-from-search">
                    {isDesktop ? (
                      <input
                        type="text"
                        id="service-search"
                        className="input-checkout"
                        placeholder={t('checkout.checkoutClient.addAnotherService')}
                        value={this.state.serviceSearchTerm}
                        onChange={this.searchServices}
                        autoComplete="off"
                        autoFocus
                      />
                    ) : (
                      <input
                        type="text"
                        id="service-search"
                        className="input-checkout"
                        placeholder={t('checkout.checkoutClient.addAnotherService')}
                        value={this.state.serviceSearchTerm}
                        onChange={this.searchServices}
                        autoComplete="off"
                      />
                    )}

                    {this.state.clearServiceBtn && this.state.serviceSearchTerm.length > 0 && <span onClick={this.clearService} role="button" tabIndex={0} className="btn-clear-search serv" />}
                    {!this.state.selectedService && this.state.filteredServices.length === 0 && this.state.serviceSearchTerm.length > 0 && (
                      <div className="dropdown-container service">
                        <div className={CN.dropdown}>
                          <div className={CN.item}>{t('common.messages.noResults')}</div>
                        </div>
                      </div>
                    )}
                    {this.state.filteredServices.length > 0 && this.state.serviceSearchTerm.length > 0 && (
                      <div className="dropdown-container service">
                        <div className={CN.dropdown}>
                          {this.state.filteredServices.map((service) => (
                            <div className={CN.item} key={service.id} onClick={() => this.selectService(service)} role="menuitem" tabIndex="0">
                              {`${service.name}`}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <button data-cy="checkout-product-button" className={!this.state.productRow ? 'btn-show-product-row' : 'hidden'} onClick={this.showProductRow}>
                    {t('checkout.checkoutClient.addRetailProduct')}
                  </button>
                  {USE_TIP_BUTTON_WITH_CONDITIONS && fromAnyToBool(!isTipButtonRendered) && fromAnyToBool(tipBtnVisible && !isEmptyString(addedServicesSorted)) && (
                    <button className={isFirstClient ? 'btn-show-tip-row' : 'hidden'} onClick={showTip}>
                      {t('checkout.checkoutClient.addTip')}
                    </button>
                  )}

                  {USE_TIP_BUTTON_FOR_TEMPORALY_TEST_BE && (
                    <button className={isFirstClient ? 'btn-show-tip-row' : 'hidden'} onClick={showTip}>
                      {t('checkout.checkoutClient.addTip')}
                    </button>
                  )}
                </div>
                <div className="cell-staff" />
                <div className="cell-price" />
                {canApplyDiscount && <div className="cell-discount" />}
                <div className="cell-total" />
                <div className="cell-last" />
              </div>
            </div>
          </div>

          {/* Product row */}
          {this.state.productRow && (
            <div className="table-checkout product-header">
              <div className="thead gray">
                <div className="cell-product">
                  <span>{t('checkout.checkoutClient.retailProducts')}</span>
                  <span className="pull-right quanity-label">{t('checkout.checkoutClient.quantity')}</span>
                </div>
                <div className="cell-staff">{t('checkout.checkoutClient.staffMember')}</div>
                <div className="cell-price">
                  <span>{t('checkout.checkoutClient.price')}</span>
                </div>
                {canApplyDiscount && <div className="cell-discount">{t('checkout.checkoutClient.discount')}</div>}
                <div className="cell-total">{t('checkout.checkoutClient.total')}</div>
                <div className="cell-last" />
              </div>
            </div>
          )}
          {addedProducts.length > 0 &&
            addedProducts.map((product, i) => (
              <CheckoutProduct
                product={product}
                key={product.id ? product.id : product.title}
                index={i}
                clientIndex={indexFromProps}
                onDelete={this.removeProduct}
                products={products}
                stylists={stylists}
              />
            ))}
          {/* Product search input */}
          {this.state.productRow && (
            <div className="select-from-search cfix">
              <input
                type="text"
                id="product-search"
                className="input-checkout"
                placeholder={addedProducts.length > 0 ? t('checkout.checkoutClient.addAnotherProduct') : t('checkout.checkoutClient.searchProducts')}
                value={this.state.productSearchTerm}
                onChange={this.searchProducts}
                autoComplete="off"
                autoFocus
              />
              {this.state.clearBtn && this.state.productSearchTerm.length > 0 && <span onClick={this.clearProduct} role="button" tabIndex={0} className="btn-clear-search" />}
              {!this.state.selectedProduct && this.state.filteredProducts.length === 0 && this.state.productSearchTerm.length > 1 && (
                <div className="dropdown-container product">
                  <div className={CN.dropdown}>
                    <ProductsMessage />
                  </div>
                </div>
              )}
              {this.state.filteredProducts.length > 0 && this.state.productSearchTerm.length > 0 && (
                <div className="dropdown-container product">
                  <div className={CN.dropdown}>
                    {this.state.filteredProducts.map((product) => (
                      <div className={CN.item} key={product.title} onClick={() => this.selectProduct(product)} role="menuitem" tabIndex="0">
                        {`${product.title}`}
                      </div>
                    ))}
                    <ProductsMessage />
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    );
  }
}

// this extra export is needed for tests that provide their own mock for connect()
export { CheckoutClient as CheckoutClient4Test };

const CheckoutClientWithHocs = withTranslation()(withTheme()(CheckoutClient));

const CheckoutClientWrapper = (props) => {
  const { canApplyDiscount } = useCheckoutPermissions();
  return <CheckoutClientWithHocs {...props} canApplyDiscount={canApplyDiscount} />;
};

const CheckoutClientHoc = connect(
  (state) => ({
    bookingData: state.checkout.bookingData,
    hasSelfStripeAccount: selectCurrentSalonHasSelfEmployedStripeAccount(state),
  }),
  (dispatch) =>
    bindActionCreators(
      {
        addService,
        removeService,
        addProduct,
        removeProduct,
        updateProductTitle,
        updateProductPrice,
      },
      dispatch,
    ),
)(CheckoutClientWrapper);

export default CheckoutClientHoc;
